import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { PushBanner } from "@/components/PushBanner";
import { CalendarView } from "@/components/CalendarView";
import { CalendarSync } from "@/components/CalendarSync";
import { CategoryFilter } from "@/components/CategoryFilter";
import { MosqueFilter } from "@/components/MosqueFilter";
import { CalendarLegend } from "@/components/CalendarLegend";
import api from "@/lib/api";
import { getGuestPrefs, setGuestPrefs } from "@/lib/guest";

export default function TenantCalendar() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assoc, setAssoc] = useState(null);
  const [mosques, setMosques] = useState([]);
  const [events, setEvents] = useState([]);
  const [range, setRange] = useState(null);
  const [cat, setCat] = useState(() => getGuestPrefs(slug).cat || "all");
  const [mosqueFilter, setMosqueFilter] = useState(searchParams.get("mosque") || getGuestPrefs(slug).mosque || "all");

  useEffect(() => {
    setGuestPrefs(slug, { cat, mosque: mosqueFilter });
  }, [slug, cat, mosqueFilter]);

  useEffect(() => {
    api.get(`/associations/${slug}`).then(({ data }) => setAssoc(data)).catch(() => {});
    api.get(`/associations/${slug}/mosques`).then(({ data }) => setMosques(data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!range) return;
    api.get(`/associations/${slug}/events`, { params: { start: range.start, end: range.end } })
      .then(({ data }) => setEvents(data))
      .catch(() => {});
  }, [slug, range]);

  const onRangeChange = useCallback((start, end) => {
    setRange((r) => (r && r.start === start && r.end === end ? r : { start, end }));
  }, []);

  if (!assoc) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar assoc={assoc} />
      <PushBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="space-y-2 min-w-0">
            <MosqueFilter mosques={mosques} value={mosqueFilter} onChange={setMosqueFilter} />
            <CategoryFilter events={events} value={cat} onChange={setCat} />
          </div>
          <div className="ml-auto">
            <CalendarSync slug={slug} />
          </div>
        </div>
        <CalendarView
          events={events.filter((e) =>
            (cat === "all" || (e.category || "other") === cat) &&
            (mosqueFilter === "all" || e.mosque_id === mosqueFilter))}
          hijriOffset={assoc.hijri_offset || 0}
          onRangeChange={onRangeChange}
          onEventClick={(e) => navigate(`/${slug}/events/${e.id}`)}
        />
        <CalendarLegend categoryColors={assoc.category_colors} events={events} />
      </div>
    </div>
  );
}
